import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import Loader from './Loader';
import useAuth from 'hooks/useAuth';
import { useGetReservation, useGetLastReservation, useGetScheduleWaitlistContinuation } from 'api/reservations';
import { useGetRestaurant } from 'api/restaurants';
import { Box, Button, Typography, Container, Stack } from '@mui/material';
import { HomeOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'hooks/useTranslation';
import axiosServices from 'utils/axios';
import { buildScheduleReadyWaiterSelectionUrl, buildScheduleWaitlistContinuationUrl } from 'utils/scheduleReadyRedirect';

function SelectWaiterRedirect() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const accessToken = token;
  const navigate = useNavigate();

  // State
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [reservationLookup, setReservationLookup] = useState<{ restaurantId: number; waitlistEntryId?: string } | null>(null);
  const [continuationPath, setContinuationPath] = useState<string | null>(null);
  const processedAccessTokenRef = useRef<string | null>(null);

  // Hooks
  const { getLastReservation, isLoading: lastReservationLoading } = useGetLastReservation();
  const { getScheduleWaitlistContinuation, isLoading: continuationLoading } = useGetScheduleWaitlistContinuation();
  const { reservation, reservationLoading } = useGetReservation(reservationId);
  const restaurantLookupId = reservation?.restaurant?.id ?? reservationLookup?.restaurantId ?? null;
  const { restaurant, restaurantLoading, restaurantError } = useGetRestaurant(restaurantLookupId);
  const { loginWithPhone } = useAuth();
  const t = useTranslation();

  // 1. Authenticate and get profile
  useEffect(() => {
    if (!accessToken) {
      setProfileLoading(false);
      return;
    }

    let isMounted = true;

    const authenticate = async () => {
      if (reservationId || processedAccessTokenRef.current === accessToken) {
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);
      setProfileError(null);

      try {
        const response = await axiosServices.get('/users/me', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!isMounted) return;

        const userData = response.data?.data;
        if (!userData?.id) {
          throw new Error('Notification token user is missing');
        }

        if (localStorage.getItem('serviceToken') !== accessToken) {
          await loginWithPhone({
            accessToken,
            userId: userData.id,
            role: userData.role || 'customer',
            waiterId: userData.waiter_id
          });
        }

        const abilities = userData?.abilities;
        const foundRestaurantId = userData?.restaurant_ids?.[0] || userData?.waiter?.restaurant_id || userData?.abilities?.restaurant_id;

        if (abilities?.reservation_id) {
          setReservationId(abilities.reservation_id);
        } else if (foundRestaurantId) {
          const nextLookup = {
            restaurantId: Number(foundRestaurantId),
            waitlistEntryId: abilities?.waitlist_entry_id || undefined
          };
          setReservationLookup((currentLookup) =>
            currentLookup?.restaurantId === nextLookup.restaurantId && currentLookup?.waitlistEntryId === nextLookup.waitlistEntryId
              ? currentLookup
              : nextLookup
          );
        }

        processedAccessTokenRef.current = accessToken;
      } catch (error) {
        console.error('Failed to authenticate:', error);
        if (isMounted) setProfileError('Failed to authenticate with token');
      } finally {
        if (isMounted) {
          setProfileLoading(false);
        }
      }
    };

    authenticate();

    return () => {
      isMounted = false;
    };
  }, [accessToken, loginWithPhone, reservationId]);

  // 2. Resolve schedule-ready continuation
  useEffect(() => {
    if (reservationId || continuationPath || !reservationLookup || !restaurant) return;

    let isMounted = true;

    const resolveContinuation = async () => {
      try {
        if (restaurant.mode === 'non_api') {
          if (!reservationLookup.waitlistEntryId) {
            throw new Error('Schedule waitlist entry is missing');
          }

          const continuation = await getScheduleWaitlistContinuation(reservationLookup.waitlistEntryId);
          if (!isMounted) return;

          if (continuation.success && continuation.data) {
            setContinuationPath(buildScheduleWaitlistContinuationUrl(continuation.data));
          } else {
            setProfileError(continuation.message);
          }
          return;
        }

        const lastRes = await getLastReservation(reservationLookup.restaurantId, reservationLookup.waitlistEntryId);
        if (!isMounted) return;

        if (lastRes.success && lastRes.data?.id) {
          setReservationId(lastRes.data.id);
        } else {
          setProfileError(lastRes.message);
        }
      } catch (error) {
        console.error(error);
        if (isMounted) setProfileError('Failed to resolve schedule waitlist continuation');
      }
    };

    resolveContinuation();

    return () => {
      isMounted = false;
    };
  }, [continuationPath, getLastReservation, getScheduleWaitlistContinuation, reservationId, reservationLookup, restaurant]);

  // Render Logic

  // 1. Loading States
  if (profileLoading) return <Loader />;

  // If we are currently fetching the last reservation to find an ID
  if ((lastReservationLoading || continuationLoading) && !reservationId && !continuationPath) return <Loader />;

  // If we have an ID and are fetching the specific reservation
  if (reservationLoading && reservationId) return <Loader />;

  // The redirect date/time must be built in the restaurant timezone.
  if ((reservation || reservationLookup) && restaurantLoading) return <Loader />;
  if (reservationLookup && !restaurantError && !profileError && !reservationId && !continuationPath) return <Loader />;

  // 2. Error States
  const lookupError = profileError || (reservationLookup && restaurantError ? 'Failed to load restaurant' : null);
  if (lookupError || !accessToken) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 3
          }}
        >
          <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
          <Stack spacing={1}>
            <Typography variant="h3" color="textPrimary">
              {t('Something went wrong')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {lookupError || t('Token is missing.')} {t('Please try again or go to the home page.')}
            </Typography>
          </Stack>
          <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
            {t('Go to Home')}
          </Button>
        </Box>
      </Container>
    );
  }

  // 3. Success Redirect
  if (continuationPath) {
    return <Navigate to={continuationPath} replace />;
  }

  if (reservation) {
    if (restaurantError || !restaurant) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 3
            }}
          >
            <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
            <Stack spacing={1}>
              <Typography variant="h3" color="textPrimary">
                {t('Something went wrong')}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {t('Please try again or go to the home page.')}
              </Typography>
            </Stack>
            <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
              {t('Go to Home')}
            </Button>
          </Box>
        </Container>
      );
    }

    const redirect = buildScheduleReadyWaiterSelectionUrl(reservation, restaurant.timezone || 'Europe/Madrid');
    if (!redirect.ok) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 3
            }}
          >
            <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
            <Stack spacing={1}>
              <Typography variant="h3" color="textPrimary">
                {t('Reservation not found')}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {t('We could not find the reservation associated with this link.')} {t('Please try again or go to the home page.')}
              </Typography>
            </Stack>
            <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
              {t('Go to Home')}
            </Button>
          </Box>
        </Container>
      );
    }

    return <Navigate to={redirect.path} replace />;
  }

  // 4. Not Found (Final Fallback if everything loaded but no reservation found)
  if (!reservationId && !profileLoading && !lastReservationLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 3
          }}
        >
          <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
          <Stack spacing={1}>
            <Typography variant="h3" color="textPrimary">
              {t('Reservation not found')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('We could not find the reservation associated with this link.')} {t('Please try again or go to the home page.')}
            </Typography>
          </Stack>
          <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
            {t('Go to Home')}
          </Button>
        </Box>
      </Container>
    );
  }

  // 5. Catch-all loader (e.g. while reservationId is set but useGetReservation hasn't returned loading=true yet)
  return <Loader />;
}

export default SelectWaiterRedirect;
